export const GOOGLE_ADS_ID = 'AW-18354365655';
/** Propiedad de GA4. Comparte etiqueta con Ads: un solo gtag.js configura las dos. */
export const GA4_ID = 'G-CX4GLJ61J5';
const PURCHASE_DESTINATION = `${GOOGLE_ADS_ID}/DE_7COHjvNccENfJhbBE`;
const CONSENT_STORAGE = 'permiso-vtc.google-ads-consent';
const TRACKED_STORAGE = 'permiso-vtc.google-ads-conversions';
const GOOGLE_TAG_SCRIPT_ID = 'permiso-vtc-google-tag';

export type AdsConsent = 'granted' | 'denied' | null;

export interface PurchaseConversion {
  transactionId: string;
  value: number;
  currency: string;
}

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: Gtag;
  }
}

function ensureConsentDefaults(): Gtag {
  window.dataLayer ??= [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments);
    };
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });
  }
  return window.gtag;
}

export function getAdsConsent(): AdsConsent {
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE);
    return stored === 'granted' || stored === 'denied' ? stored : null;
  } catch {
    return null;
  }
}

function updateConsent(choice: Exclude<AdsConsent, null>): void {
  const gtag = ensureConsentDefaults();
  const granted = choice === 'granted' ? 'granted' : 'denied';
  gtag('consent', 'update', {
    ad_storage: granted,
    analytics_storage: granted,
    ad_user_data: granted,
    ad_personalization: 'denied',
  });
}

/**
 * Carga la etiqueta de Google. Se llama SIEMPRE, con o sin consentimiento: es
 * el modo avanzado del consent mode. Sin consentimiento la etiqueta no escribe
 * cookies ni identifica a nadie, pero envía señales anónimas que permiten a
 * Google modelar las conversiones que no puede observar. En el modo básico
 * (no cargar nada hasta el «acepto») esas visitas simplemente no existen y la
 * puja automática aprende con la mitad de los datos.
 */
function loadGoogleTag(): void {
  const gtag = ensureConsentDefaults();
  if (!document.getElementById(GOOGLE_TAG_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GOOGLE_TAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(script);
    gtag('js', new Date());
    // `send_page_view: false` porque la vista de página la manda App.tsx en
    // cada cambio de ruta. Sin esto se contarían dos por carga: la automática
    // de la etiqueta y la nuestra, y toda la analítica saldría inflada.
    gtag('config', GA4_ID, { send_page_view: false });
    gtag('config', GOOGLE_ADS_ID);
  }
}

function trackedTransactions(): string[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(TRACKED_STORAGE) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

function markTransactionTracked(transactionId: string): void {
  try {
    const recent = [...new Set([...trackedTransactions(), transactionId])].slice(-50);
    window.localStorage.setItem(TRACKED_STORAGE, JSON.stringify(recent));
  } catch {
    /* La deduplicación de Google por transaction_id sigue activa. */
  }
}

function sendPurchaseConversion(conversion: PurchaseConversion): void {
  if (trackedTransactions().includes(conversion.transactionId)) return;
  loadGoogleTag();
  window.gtag?.('event', 'conversion', {
    send_to: PURCHASE_DESTINATION,
    value: conversion.value,
    currency: conversion.currency,
    transaction_id: conversion.transactionId,
  });
  // La misma compra, en GA4, para poder ver el embudo completo hasta el euro.
  window.gtag?.('event', 'purchase', {
    transaction_id: conversion.transactionId,
    value: conversion.value,
    currency: conversion.currency,
  });
  markTransactionTracked(conversion.transactionId);
}

export function setAdsConsent(choice: Exclude<AdsConsent, null>): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE, choice);
  } catch {
    /* La elección sigue aplicándose durante esta carga aunque no pueda persistirse. */
  }
  updateConsent(choice);
  loadGoogleTag();
}

export function initializeGoogleAds(): void {
  ensureConsentDefaults();
  const consent = getAdsConsent();
  if (consent) updateConsent(consent);
  loadGoogleTag();
}

/**
 * Envía un evento del embudo. No comprueba el consentimiento a propósito: la
 * etiqueta ya está cargada y respeta por sí misma el estado de `consent`, de
 * modo que sin permiso el evento viaja anónimo y sin cookies.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  ensureConsentDefaults();
  window.gtag?.('event', name, params);
}

export function trackPurchaseConversion(conversion: PurchaseConversion): void {
  if (
    !conversion.transactionId.startsWith('cs_') ||
    !Number.isFinite(conversion.value) ||
    conversion.value <= 0 ||
    !/^[A-Z]{3}$/.test(conversion.currency)
  ) {
    return;
  }
  // En modo avanzado la conversión se envía siempre: es la etiqueta la que
  // decide, según el estado de `consent`, si puede identificar al usuario o
  // si la manda anónima para que Google la modele.
  sendPurchaseConversion(conversion);
}
